import { Injectable, Signal } from '@angular/core';
import { getAccountUserQuery, getUserAvatarQuery } from '@graphql/account';
import { uiSettingsQuery } from '@graphql/general';
import { TranslateService } from '@ngx-translate/core';
import { CognitoService } from '@services/cognito/cognito.service';
import { ThemeService } from '@services/ui/theme.service';
import { ToastService } from '@services/ui/toast.service';
import { getErrorMessage } from '@utils/errors';
import { Apollo } from 'apollo-angular';
import { persistentSignal } from 'app/signals/persistent-signal';
import {
  GetAccountUserQuery,
  GetUserAvatarQuery,
  GetUserAvatarQueryVariables,
  UiSettings,
  UiSettingsQuery,
  UiSettingsQueryVariables,
  UserAccount,
} from 'gql/graphql';
import { firstValueFrom } from 'rxjs';

const USER_TIMEZONE = 'user-timezone';
const USER_UI_SETTINGS = 'user-ui-settings';
const USER_ACCOUNT = 'user-account';

@Injectable({
  providedIn: 'root',
})
export class UserSettingsService {
  private userTimezone = persistentSignal<string>('', {
    key: USER_TIMEZONE,
  });
  private userUiSettings = persistentSignal<UiSettings | null>(null, {
    key: USER_UI_SETTINGS,
  });
  private _userAccount = persistentSignal<UserAccount | null>(null, {
    key: USER_ACCOUNT,
  });

  timezone: Signal<string>;
  uiSettings: Signal<UiSettings | null>;
  userAccount: Signal<UserAccount | null>;
  constructor(
    private readonly apollo: Apollo,
    private readonly translate: TranslateService,
    private readonly toast: ToastService,
    private readonly theme: ThemeService,
    private readonly cognitoService: CognitoService
  ) {
    this.timezone = this.userTimezone.asReadonly();
    this.uiSettings = this.userUiSettings.asReadonly();
    this.userAccount = this._userAccount.asReadonly();
  }

  public setUserTimezone(timezone: string) {
    this.userTimezone.set(timezone);
  }
  public setUserUiSettings(settings: UiSettings | null) {
    this.userUiSettings.set(settings);
    if (settings) {
      this.applyThemeSettings(settings);
    }
  }
  public updateCustomLogo(customLogo: string | null) {
    this.userUiSettings.update((prev) =>
      prev
        ? {
            ...prev,
            logo: { abb: customLogo ? false : true, custom: customLogo },
          }
        : null
    );
  }
  public async getUserAvatar(): Promise<void> {
    return new Promise((resolve, reject) => {
      firstValueFrom(
        this.apollo.watchQuery<GetUserAvatarQuery, GetUserAvatarQueryVariables>(
          {
            query: getUserAvatarQuery,
          }
        ).valueChanges
      )
        .then((res) => {
          this.cognitoService.updateLoggedInUserImgUrl(
            res.data.getUserAvatar ?? ''
          );
          resolve();
        })
        .catch((err) => {
          const errMsg = getErrorMessage(err, this.translate);
          this.toast.notifyError(errMsg);
          reject(new Error(err));
        });
    });
  }
  public async checkUIUserSettings(): Promise<void> {
    const ui = this.uiSettings();
    let settings: UiSettings;

    if (!!ui) {
      settings = ui;
    } else {
      try {
        const { data } = await firstValueFrom(
          this.apollo.watchQuery<UiSettingsQuery>({
            query: uiSettingsQuery,
          }).valueChanges
        );

        let customLogo = data.uiSettings.logo.custom ?? null;
        if (customLogo && !customLogo.startsWith('data:image/')) {
          customLogo = 'data:image/png;base64,' + customLogo;
        }

        settings = {
          background: {
            abb: !!data.uiSettings.background.abb,
            custom: data.uiSettings.background.custom,
          },
          theme: {
            abb: !!data.uiSettings.theme.abb,
            custom: data.uiSettings.theme.custom,
          },
          text: {
            abb: !!data.uiSettings.text.abb,
            custom: data.uiSettings.text.custom,
          },
          logo: {
            abb: !data.uiSettings.logo.custom,
            custom: customLogo,
          },
        };

        this.setUserUiSettings(settings);
      } catch (err) {
        console.error(err);
        const errMsg = getErrorMessage(err, this.translate);
        this.toast.notifyError(errMsg);
        return;
      }
    }

    this.applyThemeSettings(settings);
  }

  cleanUiSettings(): void {
    this.userUiSettings.set(null);
    // this.applyThemeSettings(settings);
  }

  private applyThemeSettings(settings: UiSettings): void {
    const { background, theme, text } = settings;

    if (!background.abb && background.custom) {
      this.theme.setBackgroundColor('custom', background.custom);
    } else {
      this.theme.setBackgroundColor('default');
    }

    if (!theme.abb && theme.custom) {
      this.theme.setTheme('custom', theme.custom);
    } else {
      this.theme.setTheme('default');
    }

    if (!text.abb && text.custom) {
      this.theme.setTextColor('custom', text.custom);
    } else {
      this.theme.setTextColor('default');
    }
  }

  public setUserAccount(account: UserAccount | null): void {
    this._userAccount.set(account);
  }

  public getUserAccount(): Promise<UserAccount | null> {
    return new Promise((resolve, reject) => {
      firstValueFrom(
        this.apollo.watchQuery<GetAccountUserQuery>({
          query: getAccountUserQuery,
        }).valueChanges
      )
        .then((res) => {
          const account = res.data.getUserAccount;
          this.setUserAccount(account);
          resolve(account);
        })
        .catch((err) => {
          console.error(err);
          const errMsg = getErrorMessage(err, this.translate);
          this.toast.notifyError(errMsg);
          resolve(null);
        });
    });
  }

  public getUserUISettings(): Promise<void> {
    return new Promise((resolve, reject) => {
      firstValueFrom(
        this.apollo.watchQuery<UiSettingsQuery, UiSettingsQueryVariables>({
          query: uiSettingsQuery,
        }).valueChanges
      )
        .then((res) => {
          let uiSettings = res.data.uiSettings ?? null;
          // Aggiungi il prefisso base64 se serve
          if (
            uiSettings &&
            uiSettings.logo &&
            uiSettings.logo.custom &&
            uiSettings.logo.abb === false &&
            !uiSettings.logo.custom.startsWith('data:image/')
          ) {
            uiSettings = {
              ...uiSettings,
              logo: {
                ...uiSettings.logo,
                custom: 'data:image/png;base64,' + uiSettings.logo.custom,
              },
            };
          }
          this.setUserUiSettings(uiSettings);
          resolve();
        })
        .catch((err) => {
          console.error(err);
          const errMsg = getErrorMessage(err, this.translate);
          this.toast.notifyError(errMsg);
          reject(new Error(err));
        });
    });
  }
}
